import {
  DepositChainConfig,
  DepositTokenSymbol,
  ETHEREUM_DEPOSIT_CHAIN,
  ARBITRUM_DEPOSIT_CHAIN,
  BASE_DEPOSIT_CHAIN,
  INK_DEPOSIT_CHAIN,
  USDC_INK_ADDRESS,
} from './chains';

export type FundingTokenSymbol = 'USDT' | 'USDC';

export type AcrossArrivalStatus =
  | 'pending'
  | 'delivered'
  | 'partial_arrival'
  | 'arrival_timeout';

export interface AcrossFundingRoute {
  sourceChainId: number;
  sourceChain: DepositChainConfig;
  uiToken: FundingTokenSymbol;
  sourceTokenAddress: `0x${string}`;
  inkTokenSymbol: Extract<DepositTokenSymbol, 'USDT0' | 'USDC'>;
  outputTokenAddress: `0x${string}`;
  productId: 0 | 5;
  tokenDecimals: 6;
}

export interface AcrossArrivalInput {
  expectedOutputAmount: bigint;
  preBridgeInkBalance: bigint;
  currentInkBalance?: bigint;
  submittedAtMs: number;
  nowMs: number;
  hasVerifiedFill: boolean;
  timeoutMs?: number;
}

export interface AcrossArrivalResult {
  status: AcrossArrivalStatus;
  delta: bigint;
}

const INK_USDT0_ADDRESS = INK_DEPOSIT_CHAIN.tokens.find((token) => token.symbol === 'USDT0')
  ?.tokenAddress as `0x${string}`;

const FUNDING_TOKENS: FundingTokenSymbol[] = ['USDT', 'USDC'];

const SOURCE_CHAINS = [
  ETHEREUM_DEPOSIT_CHAIN,
  ARBITRUM_DEPOSIT_CHAIN,
  BASE_DEPOSIT_CHAIN,
];

function buildRoute(
  sourceChain: DepositChainConfig,
  uiToken: FundingTokenSymbol,
): AcrossFundingRoute {
  const sourceToken = sourceChain.tokens.find((token) => {
    if (uiToken === 'USDT') return token.symbol === 'USDT' || token.symbol === 'USDT0';
    return token.symbol === uiToken;
  });

  if (!sourceToken) {
    throw new Error(`Missing ${uiToken} token config for ${sourceChain.name}`);
  }

  return {
    sourceChainId: sourceChain.chainId,
    sourceChain,
    uiToken,
    sourceTokenAddress: sourceToken.tokenAddress,
    inkTokenSymbol: uiToken === 'USDT' ? 'USDT0' : 'USDC',
    outputTokenAddress: uiToken === 'USDT' ? INK_USDT0_ADDRESS : USDC_INK_ADDRESS,
    productId: uiToken === 'USDT' ? 0 : 5,
    tokenDecimals: 6,
  };
}

export const ACROSS_FUNDING_ROUTES: AcrossFundingRoute[] = SOURCE_CHAINS.flatMap((sourceChain) =>
  FUNDING_TOKENS.map((uiToken) => buildRoute(sourceChain, uiToken)),
);

export function getFundingTokenSymbols(): FundingTokenSymbol[] {
  return [...FUNDING_TOKENS];
}

export function getFundingSourceChainsForToken(
  uiToken: FundingTokenSymbol,
): DepositChainConfig[] {
  return ACROSS_FUNDING_ROUTES.filter((route) => route.uiToken === uiToken).map(
    (route) => route.sourceChain,
  );
}

export function resolveFundingRoute(
  sourceChainId: number,
  uiToken: FundingTokenSymbol,
): AcrossFundingRoute | undefined {
  return ACROSS_FUNDING_ROUTES.find(
    (route) => route.sourceChainId === sourceChainId && route.uiToken === uiToken,
  );
}

export function isFundingTokenSymbol(symbol: string): symbol is FundingTokenSymbol {
  return symbol === 'USDT' || symbol === 'USDC';
}

export function deriveAcrossArrivalState({
  expectedOutputAmount,
  preBridgeInkBalance,
  currentInkBalance,
  submittedAtMs,
  nowMs,
  hasVerifiedFill,
  timeoutMs = 180_000,
}: AcrossArrivalInput): AcrossArrivalResult {
  const delta =
    currentInkBalance !== undefined && currentInkBalance > preBridgeInkBalance
      ? currentInkBalance - preBridgeInkBalance
      : BigInt("0");

  if (expectedOutputAmount > BigInt("0") && delta >= expectedOutputAmount) {
    return { status: 'delivered', delta };
  }

  if (hasVerifiedFill && delta > BigInt("0")) {
    return { status: 'delivered', delta };
  }

  if (nowMs - submittedAtMs >= timeoutMs) {
    return {
      status: delta > BigInt("0") ? 'partial_arrival' : 'arrival_timeout',
      delta,
    };
  }

  return { status: 'pending', delta };
}

export function calculateAcrossAutoDepositAmount({
  expectedOutputAmount,
  targetDepositAmount,
  preBridgeInkBalance,
  currentInkBalance,
  nadoAllowance,
}: {
  expectedOutputAmount: bigint;
  targetDepositAmount?: bigint;
  preBridgeInkBalance: bigint;
  currentInkBalance: bigint;
  nadoAllowance?: bigint;
}): bigint {
  const delta =
    currentInkBalance > preBridgeInkBalance
      ? currentInkBalance - preBridgeInkBalance
      : BigInt("0");

  let depositAmount = expectedOutputAmount < delta ? expectedOutputAmount : delta;
  if (targetDepositAmount !== undefined && targetDepositAmount < depositAmount) {
    depositAmount = targetDepositAmount;
  }
  if (nadoAllowance !== undefined && nadoAllowance > BigInt("0") && nadoAllowance < depositAmount) {
    depositAmount = nadoAllowance;
  }
  return depositAmount;
}

export function calculateAcrossGrossInputAmount({
  targetOutputAmount,
  quotedInputAmount,
  quotedOutputAmount,
  safetyBufferAmount = BigInt("1000"),
}: {
  targetOutputAmount: bigint;
  quotedInputAmount: bigint;
  quotedOutputAmount: bigint;
  safetyBufferAmount?: bigint;
}): bigint {
  if (quotedOutputAmount >= targetOutputAmount) {
    return quotedInputAmount;
  }

  return quotedInputAmount + (targetOutputAmount - quotedOutputAmount) + safetyBufferAmount;
}

export function toNadoProductAmount({
  amount,
  // Endpoint deposit amounts are token-native raw units for the selected
  // product, not protocol-wide 18-decimal fixed-point units.
}: {
  amount: bigint;
  tokenDecimals: number;
}): bigint {
  return amount;
}
